// @flow
import React from 'react';
import 'app/common/lib/PureReactResource';
import AdTracking from 'app/common/lib/AdTracking';
import App from 'app/common/react/components/App';
import AppVersion from 'app/common/lib/AppVersion';
import augmentLocaleData from 'app/common/lib/localeLoader/augmentLocaleData';
import autoLoginLoader from 'app/www-unauth/components/autoLoginLoader';
import buildExperimentsClients from 'app/common/lib/buildExperimentsClients';
import configureStore from 'app/www-unauth/redux/store/configureStore';
import context from 'app/common/lib/context';
import Csrf from 'app/common/lib/Csrf';
import focusUtil from 'app/common/lib/focusUtil';
import i18n from 'app/common/lib/i18n';
import localeLoader from 'app/common/lib/localeLoader/lite';
import Logging from 'app/common/lib/Logging';
import logStats from 'app/common/lib/logStats';
import LogTraffic from 'app/common/lib/LogTraffic';
import makeI18n from 'app/common/lib/makeI18n';
import pageInfo from 'app/common/lib/pageInfo';
import Pc from 'app/common/lib/Pc';
import populateStore from 'app/common/redux/store/populateStore';
import pwaType from 'app/common/lib/pwaType';
import registerPageLoadEventListeners from 'app/common/lib/TraceLogger/registerPageLoadEventListeners';
import renderRoot from 'app/common/lib/react/renderRoot';
import ReportMetrics from 'app/common/lib/ReportMetrics';
import routes from 'app/www-unauth/routes';
import store from 'app/common/redux/store/singleton';
import timeSpentManager from 'app/common/lib/react/TimeSpentManager';
import traceLogger from 'app/packages/trace-logger/singleton';
import type { UnauthStartArgs } from 'app/common/types/StartArgs';
import urlUtil from 'app/common/lib/urlUtil';
import user from 'app/common/lib/User';
import Viewport from 'app/common/lib/denzel/Viewport';
import { RequestContextFactory } from 'app/common/lib/RequestContext';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { Router, browserHistory, match } from 'react-router';
// import sendActiveUserPing from 'app/common/lib/react/sendActiveUserPing';

const logLevel = Pc.DEBUG ? Logging.LOG_LEVEL.INFO : Logging.LOG_LEVEL.WARN;
Logging.setDefaultLogLevel(logLevel);

function buildStore(context, initialState) {
  if (initialState && initialState.rebuildStoreOnClient) {
    const storeInstance = configureStore(context);
    const { resourceResponses, routeData } = initialState;
    populateStore(storeInstance, routeData, resourceResponses, null, null);
    return storeInstance;
  } else {
    const storeInstance = configureStore(context, initialState);
    return storeInstance;
  }
}

const main = {
  start(startArgs: UnauthStartArgs) {
    localeLoader(startArgs.locale)
      .then((localeData) => augmentLocaleData(localeData, false, Pc.copytune_experiments))
      .then((fixedLocaleData) => {
        i18n.set(makeI18n(startArgs.locale, fixedLocaleData));
        main.loaded(startArgs);
      });
  },

  loaded(startArgs: UnauthStartArgs) {
    Viewport.init();

    Csrf.cleanUpCookie();

    user.set(startArgs.context.user);
    context.set(startArgs.context);
    context.setInitialPageContext(startArgs.initialPageContext);
    context.updateClientOnlyValue('previousMainModuleName', '');
    context.updateClientOnlyValue('landingPagePinId', '');

    const rq = RequestContextFactory.fromGlobalContext();

    const logStatsKeepAliveExp = new SingleTreatmentExp(rq, 'mweb_web_logstats_keepalive');
    const stickyKeepAliveFallbackExp = new SingleTreatmentExp(
      rq,
      'mweb_web_logstats_stickykeepalivefallback',
    );
    logStats.initBrowser({
      useKeepAlive: logStatsKeepAliveExp.enabled(),
      stickyKeepAliveFallback: stickyKeepAliveFallbackExp.enabledWithNoActivation(),
    });

    const storeInstance = buildStore(startArgs.context, window.__INITIAL_STATE__);
    store.set(storeInstance);

    if (startArgs.context.page_type === 'board' || startArgs.context.page_type === 'pin') {
      const location = Object.assign({}, window.location, urlUtil.getQueryStringParams());
      autoLoginLoader.attemptLogin(rq, storeInstance.dispatch, location);
    }

    if (pwaType(window) === 'windows') {
      // NOTE: Activating all users loading in windows pwa
      // to gather volumetric data (no direct control group for comparison)
      new SingleTreatmentExp(rq, 'unauth_eg_windows_pwa').enabled();
    } else {
      // NOTE: Activating all web users so we can use the data on the dashboard
      // and compare the different metrics with the pwa experiment.
      new SingleTreatmentExp(rq, 'unauth_eg_windows_pwa_control').enabled();
    }

    pageInfo.set(startArgs.initialPageInfo);

    focusUtil.initFocus();

    LogTraffic.logTrafficRegisterOnLoad();
    ReportMetrics.reportPageLoadMetricsRegisterOnLoad();

    // Propagate trace information from the ngapp request to our client traceLogger
    traceLogger.setServerTraceData(startArgs.traceLoggerData);

    // Only register the app if document is in focus;
    // otherwise, register the app onfocus.
    if (document.hasFocus()) {
      timeSpentManager.registerApp();
    }
    timeSpentManager.addEventListeners();

    // TODO(jchan): Map out what belongs in context in the pure react world, if anything
    AppVersion.setup(context.instance.app_version);

    // Page load performance instrumentation
    // If the store contains a value for the upwtActionName for this page, emit a page load start
    // event with the metric name so the appropriate handler can start its timer. Then register
    // page listeners with perf logger callbacks that will calculate the end time.
    // TODO(jchan): traceLogger only works with registered PWT enums. Make it so we can trace any
    // page load in pintrace even if it's not an official PWT-tracked page.
    let upwtActionName = store.instance.getState().ui.mainComponent.upwtActionName;
    switch (upwtActionName) {
      case PCONST.thrift.TracingClientTypes.PWTAction.KLP_PAGE_LOAD:
        upwtActionName = PCONST.thrift.TracingClientTypes.PWTAction.KLP_PAGE_INTERACTIVE;
        break;
      case PCONST.thrift.TracingClientTypes.PWTAction.PIN_PAGE_LOAD:
        upwtActionName = PCONST.thrift.TracingClientTypes.PWTAction.PIN_PAGE_INTERACTIVE;
        break;
      case PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PAGE_LOAD:
        upwtActionName = PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PAGE_INTERACTIVE;
        break;
      case PCONST.thrift.TracingClientTypes.PWTAction.USER_PAGE_LOAD:
        upwtActionName = PCONST.thrift.TracingClientTypes.PWTAction.USER_PAGE_INTERACTIVE;
        break;
      default:
        break;
    }
    if (upwtActionName) {
      startTiming(upwtActionName, {
        spanId: traceLogger.serverTraceData.client_span_id,
        traceId: traceLogger.serverTraceData.trace_id,
      });
      registerPageLoadEventListeners(upwtActionName);
    }

    const experimentsClients = buildExperimentsClients(context.instance) || {};

    match(
      {
        history: browserHistory,
        routes: routes({
          legacy_server_context: context.instance,
          ...experimentsClients,
        }),
      },
      (error, redirectLocation, renderProps) => {
        // Store the resolved Pure React router paths to be sent out
        ReportMetrics.setPureReactRouterPaths(renderProps.routes);
        // for SSR integration testing purposes
        // don't mount client-side if server_only is passed in as a query param
        if (Object.prototype.hasOwnProperty.call(renderProps.location.query, 'server_only')) {
          return;
        }

        renderRoot(
          <App context={context.instance} {...experimentsClients} store={storeInstance}>
            <Router {...renderProps} />
          </App>,
        );
      },
    );

    AdTracking.trackConversions();
    // TODO(juliac): Figure out why this causes react markup mismatch in unauth KLP and Pin
    // sendActiveUserPing();
  },
};

export default main;
